import { Assert } from '../utils';
import _ from 'lodash';
export * from './immutable-ty';

export type Primitive = string | number | boolean | Date | null;

export type Scalar =
  | Primitive
  | { readonly [field: string]: Scalar }
  | readonly Scalar[];

export type PrimitiveTimeType = 'day' | 'month' | 'year' | 'timestamp';

export type PrimitiveAttributeType =
  | 'int'
  | 'float'
  | 'boolean'
  | 'string'
  | PrimitiveTimeType
  | 'super';

export type RangeType = {
  readonly k: 'range';
  readonly start: number;
  readonly end: number;
  readonly t: 'int';
};

export type StructType = {
  readonly k: 'struct';
  readonly fields: {
    readonly [name: string]: ExtendedAttributeType;
  };
};

export type EnumType = {
  readonly k: 'enum';
  readonly t: readonly string[];
};

export type PrimitiveType = {
  readonly k: 'primitive';
  readonly t: PrimitiveAttributeType;
};

export type ArrayType = {
  readonly k: 'array';
  readonly t: ExtendedAttributeType;
};

export type RecordType = {
  readonly k: 'record';
  readonly t: ExtendedAttributeType;
};

export type AttributeType =
  | PrimitiveType
  | ArrayType
  | EnumType
  | RangeType
  | StructType
  | RecordType;

export type ExtendedAttributeType = {
  readonly ty: AttributeType;
  readonly nullable: boolean;
  readonly tags: readonly string[];
};

export type Shorthand =
  | PrimitiveAttributeType
  | AttributeType
  | { k: 'array'; t: Shorthand }
  | { k: 'struct'; fields: { readonly [name: string]: Shorthand } }
  | { ty: PrimitiveAttributeType; nullable?: boolean; tags?: readonly string[] }
  | ExtendedAttributeType;

export const shorthandIsETy = (ty: Shorthand): ty is ExtendedAttributeType => {
  return (
    typeof ty !== 'string' && 'ty' in ty && 'nullable' in ty && 'tags' in ty
  );
};

export type Row = { readonly [name: string]: Scalar };

export const PRIMITIVE_ATTRIBUTE_TYPES = [
  'int',
  'float',
  'boolean',
  'string',
  'year',
  'month',
  'day',
  'timestamp',
  'super',
] as const;

export type ShorthandToTsTy<ShorhandTy extends Shorthand> =
  ShorhandTy extends 'string'
    ? string
    : ShorhandTy extends 'boolean'
    ? boolean
    : ShorhandTy extends 'timestamp'
    ? Date
    : ShorhandTy extends 'int'
    ? number
    : ShorhandTy extends 'float'
    ? number
    : Scalar;

/**
 * Returns the ExtendAttributeType that represents the given shorthand.
 */
export const shorthandToTy = (
  shorthand: Shorthand,
  opts?: { readonly nullable?: boolean }
): ExtendedAttributeType => {
  if (shorthandIsETy(shorthand)) {
    return shorthand;
  }

  if (typeof shorthand === 'string') {
    return { ty: { k: 'primitive', t: shorthand }, nullable: true, tags: [] };
  }

  if ('k' in shorthand) {
    if (shorthand.k === 'primitive') {
      return { ty: shorthand, nullable: true, tags: [] };
    }

    if (shorthand.k === 'struct') {
      const fields = _.mapValues(shorthand.fields, (x) => shorthandToTy(x));
      return {
        ty: { k: 'struct', fields },
        nullable: true,
        tags: [],
      };
    } else if (shorthand.k === 'array') {
      return {
        ty: { k: 'array', t: shorthandToTy(shorthand.t) },
        nullable: true,
        tags: [],
      };
    } else if (shorthand.k === 'enum') {
      return {
        ty: { k: 'enum', t: Object.freeze(shorthand.t) },
        nullable: true,
        tags: [],
      };
    } else if (shorthand.k === 'range') {
      return { ty: shorthand, nullable: true, tags: [] };
    } else if (shorthand.k === 'record') {
      return { ty: shorthand, nullable: true, tags: [] };
    } else {
      return Assert.unreachable(shorthand);
    }
  }

  const { ty, nullable = true } = shorthand;

  return {
    tags: shorthand.tags ?? [],
    ty: typeof ty === 'string' ? { k: 'primitive' as const, t: ty } : ty,
    nullable: opts?.nullable !== undefined ? opts.nullable : nullable,
  };
};

export const ty = shorthandToTy;

export const displayTy = (shorthand: Shorthand): string => {
  const { ty, nullable, tags } = shorthandToTy(shorthand);

  let inner: string;

  const { k } = ty;

  switch (k) {
    case 'primitive':
      inner = ty.t;
      break;
    case 'array':
      inner = `ARRAY(${displayTy(ty.t)})`;
      break;
    case 'enum':
      inner = `ENUM(${ty.t
        .map((x) => `'${x.replaceAll(`'`, `\\'`)}'`)
        .join(', ')})`;
      break;
    case 'range':
      inner = `RANGE(${ty.start}, ${ty.end})`;
      break;
    case 'record':
      inner = `RECORD(${displayTy(ty.t)})`;
      break;
    case 'struct':
      inner = `STRUCT({ ${Object.entries(ty.fields)
        .map(([name, ty]) => `${escapeStructKey(name)}: ${displayTy(ty)}`)
        .join(', ')} })`;
      break;
  }

  const displayTags =
    tags.length > 0
      ? ` ${tags
          .map(
            (tag) =>
              `@${tag.match(/^[a-zA-Z][a-zA-Z_-\d]*$/) ? tag : `"${tag}"`}`
          )
          .join(' ')}`
      : '';
  const displayNullable = nullable ? '' : '!';
  return `${inner}${displayNullable}${displayTags}`;
};

const INDENT_WIDTH = 2;

/**
 * Returns a pretty-printed string representation of the type with proper indentation.
 * Useful for displaying complex nested types in UIs.
 *
 * @example
 * // Simple type
 * prettyDisplayTy('string') // => 'string'
 *
 * // Nested struct
 * prettyDisplayTy({
 *   k: 'struct',
 *   fields: {
 *     name: 'string',
 *     address: { k: 'struct', fields: { city: 'string', zip: 'string' } }
 *   }
 * })
 * // =>
 * // STRUCT({
 * //   name: string,
 * //   address: STRUCT({
 * //     city: string,
 * //     zip: string
 * //   })
 * // })
 */
export const prettyDisplayTy = (
  shorthand: Shorthand,
  indent: number = 0
): string => {
  const { ty, nullable, tags } = shorthandToTy(shorthand);
  const spaces = ' '.repeat(indent);
  const innerSpaces = ' '.repeat(indent + INDENT_WIDTH);

  let inner: string;

  const { k } = ty;

  switch (k) {
    case 'primitive':
      inner = ty.t;
      break;
    case 'array': {
      // Pass increased indent to nested type
      const elementType = prettyDisplayTy(ty.t, indent + INDENT_WIDTH);
      // If element type is multi-line, wrap it on its own line
      if (elementType.includes('\n')) {
        inner = `ARRAY(\n${innerSpaces}${elementType}\n${spaces})`;
      } else {
        inner = `ARRAY(${elementType})`;
      }
      break;
    }
    case 'enum':
      inner = `ENUM(${ty.t
        .map((x) => `'${x.replaceAll(`'`, `\\'`)}'`)
        .join(', ')})`;
      break;
    case 'range':
      inner = `RANGE(${ty.start}, ${ty.end})`;
      break;
    case 'record': {
      // Pass increased indent to nested type
      const valueType = prettyDisplayTy(ty.t, indent + INDENT_WIDTH);
      // If value type is multi-line, wrap it on its own line
      if (valueType.includes('\n')) {
        inner = `RECORD(\n${innerSpaces}${valueType}\n${spaces})`;
      } else {
        inner = `RECORD(${valueType})`;
      }
      break;
    }
    case 'struct': {
      const entries = Object.entries(ty.fields);
      if (entries.length === 0) {
        inner = 'STRUCT({})';
      } else {
        const fields = entries.map(([name, fieldTy]) => {
          const fieldType = prettyDisplayTy(fieldTy, indent + INDENT_WIDTH);
          return `${innerSpaces}${escapeStructKey(name)}: ${fieldType}`;
        });
        inner = `STRUCT({\n${fields.join(',\n')}\n${spaces}})`;
      }
      break;
    }
  }

  const displayTags =
    tags.length > 0
      ? ` ${tags
          .map(
            (tag) =>
              `@${tag.match(/^[a-zA-Z][a-zA-Z_-\d]*$/) ? tag : `"${tag}"`}`
          )
          .join(' ')}`
      : '';
  const displayNullable = nullable ? '' : '!';
  return `${inner}${displayNullable}${displayTags}`;
};

export const makeNullable = (ty: Shorthand): ExtendedAttributeType => ({
  ...shorthandToTy(ty),
  nullable: true,
});
export const makeNotNullable = (ty: Shorthand): ExtendedAttributeType => ({
  ...shorthandToTy(ty),
  nullable: false,
});

export const nn = makeNotNullable;

export const structOf = (fields: {
  readonly [name: string]: Shorthand;
}): ExtendedAttributeType =>
  shorthandToTy({
    k: 'struct',
    fields: _.mapValues(fields, (ty) => shorthandToTy(ty)),
  });

/**
 * Field-unions compatible struct types into one struct.
 *
 * Returns null if any input is non-struct, or if the same field appears with
 * incompatible types across inputs. Fields present on only some inputs keep
 * their original nullability (they are not auto-made nullable).
 */
export const mergeTypes = (
  types: readonly ExtendedAttributeType[]
): ExtendedAttributeType | null => {
  const fields: Record<string, ExtendedAttributeType> = {};
  for (const ty of types) {
    if (ty.ty.k !== 'struct') {
      return null;
    }
    for (const [name, fieldTy] of Object.entries(ty.ty.fields)) {
      const existing = fields[name];
      if (
        existing !== undefined &&
        displayTy(existing) !== displayTy(fieldTy)
      ) {
        return null;
      }
      fields[name] = fieldTy;
    }
  }
  return structOf(fields);
};

export const arrayOf = (shorthand: Shorthand): AttributeType =>
  shorthandToTy({ k: 'array', t: shorthand }).ty;

export const enumOf = (variants: readonly string[]): EnumType => ({
  k: 'enum',
  t: [...variants].sort(),
});

export const tag = (
  shorthand: Shorthand,
  newTags: string[]
): ExtendedAttributeType => {
  const { ty, nullable, tags } = shorthandToTy(shorthand);
  return {
    ty,
    nullable,
    tags: Object.freeze(_.uniq([...tags, ...newTags]).sort()),
  };
};

export const EMBEDDING = tag(arrayOf('float'), ['EMBEDDING']);
export const CARD_LT_1000_TAG = 'CARD_LT_1000';
export const CARD_LT_1000 = tag('string', [CARD_LT_1000_TAG]);

export const e = enumOf;
export const s = structOf;
export const a = arrayOf;

export const isArrayType = (attr: Shorthand): boolean => {
  const { ty } = shorthandToTy(attr);
  return ty.k === 'array';
};

export const isStructType = (attr: Shorthand): boolean => {
  const { ty } = shorthandToTy(attr);
  return ty.k === 'struct';
};

export const isRecord = (attr: Shorthand): boolean => {
  const { ty } = shorthandToTy(attr);
  return ty.k === 'record';
};

export const isSuperType = (attr: Shorthand): boolean => {
  const { ty } = shorthandToTy(attr);
  return ty.k === 'primitive' && ty.t === 'super';
};

export const hasTag = (
  ty: Shorthand,
  tags: string | readonly string[]
): boolean => {
  const ety = shorthandToTy(ty);
  const needed = typeof tags === 'string' ? [tags] : tags;
  return needed.every((tag) => ety.tags.includes(tag));
};

export const isEnum = (ty: Shorthand): boolean =>
  shorthandToTy(ty).ty.k === 'enum';

export const range = (start: number, end: number): RangeType => ({
  k: 'range',
  t: 'int',
  start,
  end,
});

export const record = (t: Shorthand): RecordType => ({
  k: 'record',
  t: shorthandToTy(t),
});

export const escapeStructKey = (key: string): string =>
  key.match(/^[a-zA-Z_]+$/) ? key : `'${key}'`;

export const enumPath = (...parts: string[]) => parts.join(ENUM_PATH_SEPERATOR);

export const ENUM_PATH_SEPERATOR = '~>';

export const NOT_APPLICABLE = 'N/A';

export type NestedEnumOutput = { [name: string]: NestedEnumOutput };

export const splitEnumVariant = (variant: string): string[] =>
  variant.split(ENUM_PATH_SEPERATOR);

export const enumToNestedObject = (ty: Shorthand): NestedEnumOutput => {
  const { ty: ety } = shorthandToTy(ty);

  Assert.assert(ety.k === 'enum', `Expected enum, got ${ety.k}`);

  const result: Record<string, NestedEnumOutput> = {};

  ety.t.forEach((item) => {
    // Split the item into parts based on the separator "~>"
    const parts = splitEnumVariant(item);
    let current = result;

    // Iterate over the parts to create nested objects
    parts.forEach((part, index) => {
      if (!(part in current)) {
        // Initialize an empty object if it doesn't exist
        current[part] = {};
      }
      // If not at the final part, drill down
      if (index < parts.length - 1) {
        current = current[part] ?? {};
      }
    });
  });

  return result;
};

export const nestedObjectToEnum = (
  obj: NestedEnumOutput,
  prefix: string = ''
): string[] => {
  let result: string[] = [];

  for (const key in obj) {
    const path = prefix ? `${prefix}~>${key}` : key;
    if (Object.keys(obj[key]!).length === 0) {
      result.push(path);
    } else {
      result = result.concat(nestedObjectToEnum(obj[key]!, path));
    }
  }

  return result.sort();
};

export type DiscrimatedScaler =
  | {
      readonly t: 'primitive';
      readonly primitive: Primitive;
    }
  | {
      readonly t: 'array';
      readonly elements: readonly Scalar[];
    }
  | {
      readonly t: 'struct';
      readonly fields: { readonly [field: string]: Scalar };
    };

export const discriminateScaler = (scaler: Scalar): DiscrimatedScaler => {
  switch (typeof scaler) {
    case 'number':
    case 'boolean':
    case 'string':
      return { t: 'primitive', primitive: scaler };

    case 'object': {
      if (scaler === null || scaler instanceof Date) {
        return { t: 'primitive', primitive: scaler };
      }

      if (isScalarArray(scaler)) {
        return {
          t: 'array',
          elements: scaler,
        };
      }

      return {
        t: 'struct',
        fields: scaler,
      };
    }

    case 'bigint':
    case 'function':
    case 'symbol':
    case 'undefined':
      throw new Error(
        'Impossible: cannot instantiate a scaler of these types.'
      );

    default:
      return Assert.unreachable(scaler);
  }
};

const isScalarArray = (scaler: Scalar): scaler is readonly Scalar[] =>
  Array.isArray(scaler);

/**
 * Coerces scalar values to match the expected type when possible.
 * Handles the common case where APIs return numbers as strings (e.g. "3" instead of 3)
 * which causes IllegalScalar when the schema expects float or int.
 */
export const coerceScalarForTy = (
  val: Scalar,
  ty: ExtendedAttributeType
): Scalar => {
  if (val === null) {
    return val;
  }

  const { ty: attrTy } = ty;

  if (attrTy.k === 'primitive') {
    if (attrTy.t === 'float' || attrTy.t === 'int') {
      if (typeof val === 'string') {
        const parsed = Number(val);
        if (!Number.isNaN(parsed)) {
          if (attrTy.t === 'int' && !Number.isInteger(parsed)) {
            return val; // Don't coerce "3.14" to int - let it fail
          }
          return parsed;
        }
      }
    }
    return val;
  }

  if (
    attrTy.k === 'struct' &&
    typeof val === 'object' &&
    !Array.isArray(val) &&
    !(val instanceof Date)
  ) {
    const result: Record<string, Scalar> = {};
    const valRecord = val as Record<string, Scalar>;
    for (const [k, fieldTy] of Object.entries(attrTy.fields)) {
      const fieldVal = valRecord[k] ?? null;
      result[k] = coerceScalarForTy(fieldVal, fieldTy);
    }
    return result;
  }

  if (attrTy.k === 'array' && Array.isArray(val)) {
    const eltTy = (attrTy as ArrayType).t;
    return val.map((v) => coerceScalarForTy(v, eltTy));
  }

  if (
    attrTy.k === 'record' &&
    typeof val === 'object' &&
    val !== null &&
    !Array.isArray(val) &&
    !(val instanceof Date)
  ) {
    const result: Record<string, Scalar> = {};
    const valueTy = (attrTy as RecordType).t;
    for (const [k, v] of Object.entries(val)) {
      result[k] = coerceScalarForTy(v as Scalar, valueTy);
    }
    return result;
  }

  return val;
};
